/**
 * COVER ART — the song's artwork area, inside the Cover Art tool panel.
 *
 * THE BOX IS NOW THE PRIMARY ARTWORK AREA, not a read-only output beside a separate
 * upload zone. Uploading used to live in its own dashed panel underneath (CoverUpload,
 * now deleted), with an "or" divider between the two, which meant the panel had two
 * artwork surfaces: one that showed the artwork and one that accepted it. People click
 * the picture. So the picture is what takes the click, the tap and the drop, and the
 * second panel is gone.
 *
 * Everything the artwork area can do, in one place:
 *   click / tap / Enter / Space  → choose an image
 *   drop an image on it          → the same path
 *   Generate (in the form)       → stages generated artwork here
 *   Remove                       → clears the song's artwork
 *   Use this artwork / Discard   → commit or throw away what is staged
 *
 * A LABEL WRAPPING A HIDDEN FILE INPUT, which is what CoverUpload used and the reason
 * it worked: the native pairing gives keyboard focus, Space/Enter activation and the
 * screen-reader announcement for free, where a div with an onClick has to reimplement
 * all three and usually reimplements two. The consequence to watch is that a button
 * *inside* a label also activates the label — so Download and Remove sit in the header
 * row above the box, and Apply/Discard below it, never within.
 *
 * `onDragOver` must preventDefault or the browser navigates to the dropped file
 * instead of handing it over. That is the default action, not a bug in the handler.
 *
 * IT SHOWS WHAT IS NOT YET APPLIED, and that is still the most important job. Both
 * routes to artwork — generate and upload — land in studioStore.pendingCover and appear
 * here first, with Apply and Discard underneath. Nothing touches the song until Apply.
 *
 * Five states, and the box says which one it is in:
 *   processing — an action is running
 *   staged     — new artwork awaiting Apply/Discard; accent border, and the caption
 *                says whether it was generated or uploaded, and from which file
 *   applied    — the song's own artwork; the caption names its source
 *   empty      — nothing applied yet
 *   drop-over  — a file is being dragged onto it
 *
 * ONE CANONICAL REFERENCE. This component holds no artwork state of its own: what it
 * draws is either the song or studioStore.pendingCover, and which *kind* of artwork to
 * draw is CoverArt's decision, because the ACTIVE SONG panel and the library have to
 * make the identical one. This box owns the framing — the label, the caption, the fade,
 * the export, the remove and the commit.
 *
 * SIMULATED on the generated path: what appears after Generate is a gradient derived
 * from a hash of the new seed, not a generated image, and the panel's notice says so.
 * The uploaded path is real — it is the user's own image.
 */
import { useState } from 'react';
import { Check, Download, Image as ImageIcon, Loader2, Trash2, Upload, X } from 'lucide-react';
import type { CSSProperties } from 'react';
import { Btn, IconButton, Tooltip } from '../ui';
import CoverArt from './CoverArt';
import { coverSourceLabel, hasCoverArt } from '../../lib/artwork';
import { STUDIO_COVER_IMAGE_POLICY } from '../../lib/coverImagePolicy';
import { firstImageFrom } from '../../lib/imageUpload';
import { c } from '../../styles/theme';
import type { PendingCover } from '../../stores/studioStore';
import type { Song } from '../../types/domain';

export interface CoverPreviewProps {
  song: Song;
  /** True while the cover action is running. */
  processing: boolean;
  /**
   * The existing cover export, passed through from the Download Center so both
   * places call one handler. Omitted renders no action.
   */
  onDownload?: () => void;
  /** Artwork produced but not committed. Shown in place of the song's own. */
  pending?: PendingCover | null;
  /**
   * Receives an image chosen by click, tap or drop. Validation and reading happen in
   * useStudio, so a file picked either way is reported the same way. Omitted makes the
   * box a plain preview.
   */
  onUpload?: (file: File) => void;
  /** Clears the song's artwork. Omitted renders no remove action. */
  onRemove?: () => void;
  /** Commits `pending` to the song. */
  onApply?: () => void;
  /** Throws `pending` away. */
  onDiscard?: () => void;
}

/** Fills the square box. The gradient wrapper and the <img> share these. */
const FILL: CSSProperties = { position: 'absolute', inset: 0, borderRadius: 9 };

export default function CoverPreview({
  song,
  processing,
  onDownload,
  pending,
  onUpload,
  onRemove,
  onApply,
  onDiscard,
}: CoverPreviewProps) {
  /** True while a file is dragged over the box. Presentation only. */
  const [over, setOver] = useState(false);

  const staged = Boolean(pending);
  /*
    What the box draws. On the staged path a throwaway Song is built from the pending
    fields so CoverArt resolves it by exactly the same rules it will use once the
    artwork is applied — the preview cannot show something the applied state would
    render differently, because it is the same code path with the same inputs.
  */
  const shown: Song = pending
    ? { ...song, coverUrl: pending.coverUrl, artworkSeed: pending.artworkSeed }
    : song;
  const hasArt = staged || hasCoverArt(song);
  /** Uploading is off while an action runs: a second pick would race it. */
  const canUpload = Boolean(onUpload) && !processing;

  function take(list: FileList | null) {
    const file = firstImageFrom(list);
    if (file && onUpload) onUpload(file);
  }

  return (
    // A labelled region rather than a bare div: it is a named artwork panel that
    // updates on its own after an action, so it needs to be findable as one.
    <section aria-label="Cover art preview">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <p className="lyx-ws-eyebrow" style={{ margin: 0 }}>
          {staged ? 'Preview — not applied' : 'Cover art'}
        </p>

        {/*
          The two actions on the artwork the song ALREADY has. Both are hidden while
          something is staged, for the same reason: they would act on the applied cover
          while the box shows a different one, which is the wrong target for what is on
          screen. The staged state has Discard instead.

          Outside the box, never inside it — a button within the label would also open
          the file picker.
        */}
        {!staged && hasArt && !processing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {onDownload && (
              <Tooltip label="Download this artwork">
                <IconButton label="Download cover art" nativeTitle={false} onClick={onDownload}>
                  <Download size={13} color={c.text2} />
                </IconButton>
              </Tooltip>
            )}
            {onRemove && (
              <Tooltip label="Remove this artwork from the song">
                <IconButton label="Remove cover art" nativeTitle={false} onClick={onRemove}>
                  <Trash2 size={13} color={c.text2} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      {/*
        The artwork area itself. A <label>, so the visually hidden input inside is
        activated by click, tap, Enter and Space with no JavaScript and no keydown
        handler of our own.
      */}
      <label
        className="lyx-cover-preview"
        onDragOver={(e) => {
          // Required. Without it the browser handles the drop itself and navigates
          // away from the app to display the image.
          e.preventDefault();
          if (canUpload) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (canUpload) take(e.dataTransfer?.files ?? null);
        }}
        style={{
          background: over ? c.accentSoft : c.surface2,
          // The border carries the state: accent while staged, because it is what
          // distinguishes "this is your song's cover" from "this could be, if you
          // apply it"; dashed while there is nothing, so it reads as a place artwork
          // will arrive rather than as a failed image.
          border: `1px ${hasArt ? 'solid' : 'dashed'} ${
            over || staged ? c.accentBorder : hasArt ? c.border : c.borderStrong
          }`,
          cursor: canUpload ? 'pointer' : 'default',
        }}
      >
        {onUpload && (
          <input
            type="file"
            accept={STUDIO_COVER_IMAGE_POLICY.acceptAttribute}
            disabled={processing}
            className="lyx-visually-hidden"
            // An explicit name, because the label's text content is the artwork
            // itself in three of the five states. It also says which action this is:
            // over existing artwork the picker replaces rather than adds.
            aria-label={hasArt ? 'Replace cover art' : 'Upload cover art'}
            onChange={(e) => {
              take(e.target.files);
              // Cleared so picking the same file twice fires onChange the second
              // time. Without this, re-uploading after a discard silently does
              // nothing.
              e.target.value = '';
            }}
          />
        )}

        {processing ? (
          <div className="lyx-cover-preview-empty">
            <Loader2 size={18} color={c.accent} className="lyx-spin" />
            <p style={{ fontSize: 12, color: c.text2, margin: '10px 0 0' }}>Generating artwork…</p>
          </div>
        ) : hasArt ? (
          /*
            Wrapped so the label and the fade belong to the preview rather than to
            CoverArt, which the ACTIVE SONG panel also uses and where neither
            applies. The gradient IS the content here, so it is exposed as an image;
            there it is decoration inside an already-labelled button.

            Keyed on the artwork's identity so new artwork remounts and replays the
            fade. Without the key React reuses the node and a new cover appears with
            no indication that anything changed.
          */
          <div
            key={shown.coverUrl ?? shown.artworkSeed ?? song.id}
            className="lyx-cover-art"
            // The <img> branch carries its own alt text, so labelling the wrapper
            // too would announce the artwork twice.
            {...(shown.coverUrl
              ? {}
              : {
                  role: 'img',
                  'aria-label': song.coverStyle
                    ? `Cover art for ${song.title} — ${song.coverStyle} style`
                    : `Cover art for ${song.title}`,
                })}
            style={FILL}
          >
            <CoverArt song={shown} depth="hero" radius={9} />
          </div>
        ) : (
          /* The empty state. Says what to do with the box, which is now the same
             sentence as what the box is for. */
          <div className="lyx-cover-preview-empty">
            <ImageIcon size={20} color={over ? c.accent : c.text2} aria-hidden="true" />
            <p style={{ fontSize: 12.5, color: c.text1, margin: '10px 0 0', fontWeight: 500 }}>
              No cover art yet
            </p>
            <p style={{ fontSize: 11.5, color: c.text2, margin: '3px 0 0', lineHeight: 1.5 }}>
              {onUpload
                ? 'Click to upload an image, drop one here, or generate artwork — it appears here first.'
                : 'Generate one or upload your own — it appears here first.'}
            </p>
          </div>
        )}

        {/*
          The hint over existing artwork, because a picture that happens to be a
          button does not look like one. Shown on hover and on keyboard focus; on a
          touch device, where there is no hover, it is shown permanently — see the
          `hover: none` rule in global.css. aria-hidden because the input's own label
          already says it, and saying it twice is worse than not saying it.
        */}
        {canUpload && hasArt && (
          <span className="lyx-cover-hint" aria-hidden="true">
            <Upload size={11} />
            {staged ? 'Choose a different image' : 'Click or drop to replace'}
          </span>
        )}
      </label>

      {onUpload && (
        <p style={{ fontSize: 11, color: c.text2, margin: '8px 0 0', lineHeight: 1.5 }}>
          {STUDIO_COVER_IMAGE_POLICY.acceptedLabel} · up to {STUDIO_COVER_IMAGE_POLICY.maxLabel}
        </p>
      )}

      {/* ---- the caption ----
          Says what the box is showing, and this is where the three cases the brief
          asks to keep distinct are actually named: the song's current artwork and its
          source, generated artwork awaiting Apply, or an uploaded file awaiting Apply.
          The staged form includes the filename, so a user who picked the wrong file
          out of a folder of near-identical covers can tell before applying it. */}
      {hasArt && !processing && (
        <p
          style={{
            fontSize: 11,
            color: c.text2,
            margin: '8px 0 0',
            fontFamily: 'IBM Plex Mono',
            wordBreak: 'break-all',
          }}
        >
          {staged
            ? [
                pending?.source === 'uploaded' ? 'Uploaded' : 'Generated',
                pending?.filename ?? pending?.coverStyle,
              ]
                .filter(Boolean)
                .join(' · ')
            : [song.coverStyle, coverSourceLabel(song)].filter(Boolean).join(' · ')}
        </p>
      )}

      {/* ---- apply or discard ----
          THE intentional act. Rendered only with staged artwork, so there is no way
          to "apply" the cover a song already has. */}
      {staged && !processing && onApply && onDiscard && (
        <div className="lyx-cover-commit">
          <Btn onClick={onApply} style={{ flex: '1 1 auto' }}>
            <Check size={13} />
            Apply artwork
          </Btn>
          <Btn variant="secondary" onClick={onDiscard}>
            <X size={13} />
            Discard
          </Btn>
        </div>
      )}
    </section>
  );
}
